import { PRIORITY_FIELDS } from "./normalized-model-fields";

/**
 * Types de tokens dans une query string Elasticsearch
 */
export type TokenType =
  | "field"
  | "fieldChip"
  | "operator"
  | "value"
  | "quoted"
  | "range"
  | "parenthesis"
  | "text";

/**
 * Known fields that should be displayed as chips
 * This includes all priority fields from the normalized model
 */
const KNOWN_FIELD_NAMES = new Set<string>(
  PRIORITY_FIELDS
);

/**
 * Check if a field name is a known field that should be rendered as chip
 */
const isKnownField = (fieldName: string): boolean => {
  return KNOWN_FIELD_NAMES.has(fieldName);
};

export interface Token {
  type: TokenType;
  value: string;
  start: number;
  end: number;
}

/**
 * Parse une query string Elasticsearch et retourne les tokens avec leur type
 */
export function parseElasticsearchQuery(query: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const len = query.length;

  while (i < len) {
    const start = i;
    const char = query[i];

    // Ignorer les espaces
    if (/\s/.test(char)) {
      i++;
      continue;
    }

    // Parenthèses
    if (char === "(" || char === ")") {
      tokens.push({
        type: "parenthesis",
        value: char,
        start,
        end: i + 1,
      });
      i++;
      continue;
    }

    // Crochets pour les ranges
    if (char === "[" || char === "]") {
      tokens.push({
        type: "range",
        value: char,
        start,
        end: i + 1,
      });
      i++;
      continue;
    }

    // Guillemets (chaînes entre guillemets)
    if (char === '"') {
      let value = char;
      i++;
      let escaped = false;

      while (i < len) {
        if (escaped) {
          value += query[i];
          escaped = false;
          i++;
        } else if (query[i] === "\\") {
          value += query[i];
          escaped = true;
          i++;
        } else if (query[i] === '"') {
          value += query[i];
          i++;
          break;
        } else {
          value += query[i];
          i++;
        }
      }

      tokens.push({
        type: "quoted",
        value,
        start,
        end: i,
      });
      continue;
    }

    // Opérateurs (AND, OR, NOT, TO)
    const operatorMatch = query.slice(i).match(/^(AND|OR|NOT|TO)\s/i);
    if (operatorMatch) {
      const operator = operatorMatch[1];
      tokens.push({
        type: "operator",
        value: operator,
        start,
        end: i + operator.length,
      });
      i += operator.length;
      continue;
    }

    // Opérateurs de comparaison (>=, <=)
    if (char === ">" || char === "<") {
      let value = char;
      i++;
      if (i < len && query[i] === "=") {
        value += query[i];
        i++;
      }
      tokens.push({
        type: "operator",
        value,
        start,
        end: i,
      });
      continue;
    }

    // Champs (field:value)
    // On cherche un pattern comme "field:" ou "field.keyword:"
    const fieldMatch = query.slice(i).match(/^([a-zA-Z_][a-zA-Z0-9_.]*):/);
    if (fieldMatch) {
      const fieldName = fieldMatch[1];
      const fieldEnd = i + fieldName.length + 1; // +1 pour le ":"

      tokens.push({
        type: "field",
        value: fieldName + ":",
        start,
        end: fieldEnd,
      });

      i = fieldEnd;
      continue;
    }

    // Wildcards (* et ?)
    if (char === "*" || char === "?") {
      tokens.push({
        type: "value",
        value: char,
        start,
        end: i + 1,
      });
      i++;
      continue;
    }

    // Standalone colon (not part of a field:value pattern)
    // This handles cases like "@jojo:" where @ is not a valid field start
    if (char === ":") {
      tokens.push({
        type: "text",
        value: char,
        start,
        end: i + 1,
      });
      i++;
      continue;
    }

    // Valeurs numériques
    const numberMatch = query.slice(i).match(/^-?\d+(\.\d+)?/);
    if (numberMatch) {
      const number = numberMatch[0];
      tokens.push({
        type: "value",
        value: number,
        start,
        end: i + number.length,
      });
      i += number.length;
      continue;
    }

    // Dates (format ISO: YYYY-MM-DD)
    const dateMatch = query.slice(i).match(/^\d{4}-\d{2}-\d{2}/);
    if (dateMatch) {
      const date = dateMatch[0];
      tokens.push({
        type: "value",
        value: date,
        start,
        end: i + date.length,
      });
      i += date.length;
      continue;
    }

    // Texte simple (mots, caractères restants)
    // On prend tout jusqu'au prochain espace, opérateur, ou caractère spécial
    let textValue = "";
    while (i < len) {
      const currentChar = query[i];
      if (
        /\s/.test(currentChar) ||
        currentChar === "(" ||
        currentChar === ")" ||
        currentChar === "[" ||
        currentChar === "]" ||
        currentChar === ":" ||
        currentChar === '"' ||
        currentChar === ">" ||
        currentChar === "<"
      ) {
        break;
      }
      textValue += currentChar;
      i++;
    }

    if (textValue) {
      // Vérifier si c'est un opérateur qu'on a manqué
      const upperText = textValue.toUpperCase();
      if (upperText === "AND" || upperText === "OR" || upperText === "NOT" || upperText === "TO") {
        tokens.push({
          type: "operator",
          value: textValue,
          start,
          end: i,
        });
      } else if (isKnownField(textValue)) {
        // Known field name - render as chip
        tokens.push({
          type: "fieldChip",
          value: textValue,
          start,
          end: i,
        });
      } else {
        tokens.push({
          type: "text",
          value: textValue,
          start,
          end: i,
        });
      }
    }
  }

  return tokens;
}

export interface HighlightedPart {
  text: string;
  className: string;
  /** If true, this part should be rendered as a chip */
  isChip?: boolean;
}

/**
 * Applique le highlighting à une query string en retournant des éléments React
 */
export function highlightQueryString(query: string): HighlightedPart[] {
  if (!query) {
    return [];
  }

  const tokens = parseElasticsearchQuery(query);
  const parts: HighlightedPart[] = [];
  let lastIndex = 0;

  tokens.forEach((token) => {
    // Ajouter le texte avant le token s'il y en a (espaces, etc.)
    if (token.start > lastIndex) {
      const beforeText = query.slice(lastIndex, token.start);
      if (beforeText) {
        parts.push({
          text: beforeText,
          className: "",
        });
      }
    }

    // Ajouter le token avec sa classe
    // Note: on n'utilise pas font-semibold pour éviter de modifier la largeur des caractères
    // Les couleurs utilisent des variables CSS qui s'adaptent automatiquement au dark mode
    let className = "";
    let isChip = false;
    switch (token.type) {
      case "field":
        className = "lucene-syntax-field";
        break;
      case "fieldChip":
        // Field chips get special rendering
        className = "text-accent";
        isChip = true;
        break;
      case "operator":
        className = "lucene-syntax-operator";
        break;
      case "value":
        className = "lucene-syntax-value";
        break;
      case "quoted":
        className = "lucene-syntax-quoted";
        break;
      case "range":
        className = "lucene-syntax-range";
        break;
      case "parenthesis":
        className = "text-content-secondary";
        break;
      case "text":
        className = "text-content-primary";
        break;
    }

    parts.push({
      text: token.value,
      className,
      isChip,
    });

    lastIndex = token.end;
  });

  // Ajouter le texte restant après le dernier token
  if (lastIndex < query.length) {
    const remainingText = query.slice(lastIndex);
    if (remainingText) {
      parts.push({
        text: remainingText,
        className: "",
      });
    }
  }

  return parts;
}
